import { IconLock } from '@tabler/icons-react';
import { useText } from '../clients/words';
import type { Arenda } from './api';

/**
 * ПОЛОСА БЛОКИРОВКИ РЕДАКТИРОВАНИЯ (`s713` по контракту `s712`; `s719` — один редактор): кто
 * редактирует и с какого времени; своя блокировка — сколько осталось и «Продолжить»; истекла —
 * «Продолжить»; освободилось — «Редактировать»; «Перехватить», если сервер это право дал.
 *
 * Принимает результат `useRedaktor` (и прежнего `useArenda`).
 *
 * ⛔ Слова — из словаря экрана и из ответа сервера; кнопка перехвата рисуется только по признаку
 * сервера `mozhno_perehvatit`, а само право он проверяет ещё раз при нажатии.
 */
export interface DlyaPolosy {
  arenda: Arenda | null;
  est: boolean;
  ostalos: number | null;
  skoroIstechyot: boolean;
  slovo: string | null;
  kod: string | null;
  idyot: boolean;
  vzyat: (perenesti?: boolean) => Promise<boolean>;
  prodolzhit: () => Promise<void>;
  perehvatit: () => Promise<void>;
  /** Только у `useRedaktor`: редактирование свободно, но само не возьмётся. */
  svobodno?: boolean;
  /** Только у `useRedaktor`: аренда была моей и ушла. */
  poteryana?: boolean;
}

/** Запасные слова: карточка, чей словарь слов аренды не привёз, не показывает ключ вместо фразы. */
const ZAPASNYE: Record<string, string> = {
  'arenda.zanyato': 'Сейчас редактирует {имя} (с {время}). Пока блокировка действует, изменить нельзя.',
  'arenda.drugaya_vkladka': 'Вы редактируете это в другой вкладке (с {время}).',
  'arenda.perehvacheno': 'Редактирование перешло к {имя} в {время}. Изменение не сохранено, ваш черновик остался у вас.',
  'arenda.isteklo': 'Блокировка истекла из-за бездействия. Нажмите «Продолжить», чтобы редактировать дальше.',
  'arenda.skoro': 'Блокировка истечёт через {секунд} с. Нажмите «Продолжить», чтобы редактировать дальше.',
  'arenda.svobodno': 'Редактирование свободно.',
  'arenda.knopka.prodolzhit': 'Продолжить',
  'arenda.knopka.perehvatit': 'Перехватить',
  'arenda.knopka.perenesti': 'Перенести сюда',
  'arenda.knopka.redaktirovat': 'Редактировать',
};

export function PolosaArendy({ a }: { a: DlyaPolosy }) {
  const text = useText();
  const ar = a.arenda;

  const t = (klyuch: string, podstanovki?: Record<string, string | number>): string => {
    const izSlovarya = text(klyuch, podstanovki);
    if (izSlovarya !== klyuch) return izSlovarya;

    return Object.entries(podstanovki ?? {}).reduce((s, [imya, z]) => s.split(`{${imya}}`).join(String(z)), ZAPASNYE[klyuch] ?? klyuch);
  };

  const knopka = (slovo: string, onClick: () => void, testid: string) => (
    <button
      type="button"
      disabled={a.idyot}
      onClick={onClick}
      data-testid={testid}
      className="shrink-0 rounded-ops-md border border-current px-3 py-1.5 text-ops-caption font-medium disabled:opacity-50"
    >
      {slovo}
    </button>
  );

  let slovo: string | null = null;
  let deystvie: React.ReactNode = null;
  let vid: 'chuzhaya' | 'drugaya_vkladka' | 'istekla' | 'skoro' | 'svobodno' | 'otkaz' | null = null;

  if (ar === null) {
    // Состояния нет, а дверь аренды отказала (карточка закрыта правом, сбой): слово сервера.
    if (a.slovo === null) return null;
    vid = 'otkaz';
    deystvie = knopka(t('arenda.knopka.prodolzhit'), () => void a.prodolzhit(), 'arenda-prodolzhit');
  } else if (!a.est) {
    return null;
  } else if (ar.zanyata && !ar.moya) {
    vid = 'chuzhaya';
    // У меня перехватили: говорим это прямо, а не общим «редактирует», и что черновик цел.
    const perehvatil = a.poteryana === true && ar.perehvat?.kem != null ? ar.perehvat : null;
    slovo = perehvatil !== null
      ? t('arenda.perehvacheno', { имя: perehvatil.kem?.imya ?? '', время: perehvatil.kogda_display ?? '' })
      : t('arenda.zanyato', { имя: ar.vladelets?.imya ?? '', время: ar.vzyata_display ?? '' });
    if (ar.mozhno_perehvatit) deystvie = knopka(t('arenda.knopka.perehvatit'), () => void a.perehvatit(), 'arenda-perehvatit');
  } else if (ar.zanyata && ar.moya && !ar.eta_vkladka) {
    // Только прежний сервер: у нового свои вкладки не конфликтуют.
    vid = 'drugaya_vkladka';
    slovo = t('arenda.drugaya_vkladka', { время: ar.vzyata_display ?? '' });
    deystvie = knopka(t('arenda.knopka.perenesti'), () => void a.vzyat(true), 'arenda-perenesti');
  } else if (ar.istekla) {
    vid = 'istekla';
    slovo = t('arenda.isteklo');
    deystvie = knopka(t('arenda.knopka.prodolzhit'), () => void a.prodolzhit(), 'arenda-prodolzhit');
  } else if (a.skoroIstechyot && a.ostalos !== null) {
    vid = 'skoro';
    slovo = t('arenda.skoro', { секунд: a.ostalos });
    deystvie = knopka(t('arenda.knopka.prodolzhit'), () => void a.prodolzhit(), 'arenda-prodolzhit');
  } else if (a.slovo !== null && !ar.zanyata) {
    // Блокировка была моей и ушла (перехват, истечение с новым владельцем), сейчас свободно: слово сервера.
    vid = 'otkaz';
    deystvie = knopka(t('arenda.knopka.redaktirovat'), () => void a.prodolzhit(), 'arenda-prodolzhit');
  } else if (a.svobodno === true) {
    vid = 'svobodno';
    slovo = t('arenda.svobodno');
    deystvie = knopka(t('arenda.knopka.redaktirovat'), () => void a.prodolzhit(), 'arenda-prodolzhit');
  }

  if (vid === null) return null;

  // Перехватили у меня — слово сервера («перешло к …, черновик остался») важнее общего «редактирует».
  const tekst = a.slovo !== null && (vid === 'otkaz' || a.kod === 'arenda.perehvacheno' || a.kod === 'arenda.net_prava_perehvata') ? a.slovo : slovo;

  return (
    <div
      role={vid === 'skoro' || vid === 'svobodno' ? 'status' : 'alert'}
      data-testid="polosa-arendy"
      data-vid={vid}
      className="flex items-center gap-2 border-t border-ops-border bg-ops-warning-soft px-3 py-2 text-ops-caption text-ops-warning"
    >
      <IconLock size={18} className="shrink-0" />
      <span className="min-w-0 flex-1">{tekst}</span>
      {deystvie}
    </div>
  );
}
